import { memo, useMemo } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Cpu, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import {
  PERFORMANCE_BAND_LABELS,
  computeSeriesStats,
  formatMspt,
  formatTps,
  performanceBand,
  type PerformanceBand,
} from '../../utils/starchart';
import type { MsptTimeSeriesResponse } from '../../types/starchart';

const BAND_ICONS: Record<PerformanceBand, typeof CheckCircle> = {
  excellent: CheckCircle,
  good: CheckCircle,
  warning: AlertTriangle,
  critical: AlertCircle,
  unknown: HelpCircle,
};

/**
 * Static class strings, not interpolated ones: Tailwind only emits classes it can find literally
 * in the source, so a template-built `text-${tone}` compiles to nothing.
 */
const BAND_ICON_CLASSES: Record<PerformanceBand, string> = {
  excellent: 'text-green-400',
  good: 'text-blue-400',
  warning: 'text-yellow-400',
  critical: 'text-red-400',
  unknown: 'text-glass-400',
};

const BAND_BADGE_CLASSES: Record<PerformanceBand, string> = {
  excellent: 'badge badge-success',
  good: 'badge badge-info',
  warning: 'badge badge-warning',
  critical: 'badge badge-danger',
  unknown: 'badge badge-neutral',
};

interface PerformanceSummaryCardProps {
  data: MsptTimeSeriesResponse | null;
}

const Metric = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <div className="subcard p-3">
    <div className="eyebrow">{label}</div>
    <div className="text-lg font-semibold text-white tabular-nums mt-0.5">{value}</div>
    {hint && <div className="text-xs text-glass-500 mt-0.5 tabular-nums">{hint}</div>}
  </div>
);

/**
 * Tick performance over the range.
 *
 * Samples where the server reported nothing arrive as null and are excluded rather than counted
 * as zero — averaging a gap in as 0 TPS would turn a clean restart into a fake outage.
 */
export const PerformanceSummaryCard = memo(({ data }: PerformanceSummaryCardProps) => {
  const stats = useMemo(() => {
    const points = data?.data ?? [];
    return {
      tps: computeSeriesStats(points.map((p) => ({ t: p.timestamp * 1000, v: p.tps }))),
      mspt: computeSeriesStats(points.map((p) => ({ t: p.timestamp * 1000, v: p.mspt }))),
    };
  }, [data]);

  if (!data || stats.tps.valueCount === 0) {
    return (
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Tick performance" icon={Cpu} />
        <EmptyState
          bare
          compact
          icon={Cpu}
          title="No tick data"
          description="This server reported no tick timings over the selected range."
        />
      </div>
    );
  }

  const currentBand = performanceBand(stats.mspt.latest, stats.tps.latest);
  const averageBand = performanceBand(stats.mspt.average, stats.tps.average);
  const BandIcon = BAND_ICONS[currentBand];

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader title="Tick performance" icon={Cpu} />

      <div className="alert mt-3" role="status">
        <BandIcon className={cn('w-4 h-4 shrink-0', BAND_ICON_CLASSES[currentBand])} />
        <div className="flex items-center justify-between gap-3 w-full">
          <div>
            <div className="text-sm font-medium text-white">
              Latest sample: {PERFORMANCE_BAND_LABELS[currentBand]}
            </div>
            <div className="text-xs text-glass-400 tabular-nums">
              {formatTps(stats.tps.latest)} TPS · {formatMspt(stats.mspt.latest)}
            </div>
          </div>
          <span className={cn(BAND_BADGE_CLASSES[averageBand], 'shrink-0')}>
            {PERFORMANCE_BAND_LABELS[averageBand]} on average
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <Metric
          label="Average TPS"
          value={formatTps(stats.tps.average)}
          hint={`${formatTps(stats.tps.min)} – ${formatTps(stats.tps.max)}`}
        />
        <Metric
          label="Average MSPT"
          value={formatMspt(stats.mspt.average)}
          hint={`${formatMspt(stats.mspt.min)} – ${formatMspt(stats.mspt.max)}`}
        />
        <Metric label="Worst TPS" value={formatTps(stats.tps.min)} hint="Lowest single sample" />
        <Metric label="Worst MSPT" value={formatMspt(stats.mspt.max)} hint="Slowest single tick" />
      </div>

      <p className="text-xs text-glass-500 mt-3">
        Excellent is 19.5 TPS or better and 50ms or less per tick. Good reaches 18 TPS, warning 15,
        and anything below that is critical.
      </p>
    </div>
  );
});

PerformanceSummaryCard.displayName = 'PerformanceSummaryCard';
